import { Trans } from "@lingui/react/macro"
import { Badge, Group, Modal, Stack, Text } from "@mantine/core"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

const BAR_COUNT = 14

/** Boost small real-world peaks so the meter reads clearly (sqrt curve). */
function fillFraction(peak: number): number {
  return Math.min(1, Math.sqrt(Math.max(0, peak)) * 1.6)
}

function Meter({ peak }: { peak: number }): React.JSX.Element {
  const lit = Math.round(fillFraction(peak) * BAR_COUNT)
  return (
    <span className={classes.meter} aria-hidden>
      {Array.from({ length: BAR_COUNT }, (_, i) => (
        <span key={i} data-on={i < lit} style={{ height: `${40 + (60 * i) / (BAR_COUNT - 1)}%` }} />
      ))}
    </span>
  )
}

/**
 * Output picker for the system-audio (remote) channel. Lists the render endpoints
 * with a live level meter each — "pick the one that moves when someone talks" —
 * so a non-technical user can choose the right output without understanding audio
 * routing. Dumb: levels/targets/selection come from the smart wrapper.
 */
export const SystemAudioPickerView = ({
  opened,
  targets,
  levels,
  currentId,
  onSelect,
  onClose,
}: IProps): React.JSX.Element => {
  const peakFor = (id: string): number => levels.find((l) => l.id === id)?.peak ?? 0

  return (
    <Modal opened={opened} onClose={onClose} centered title={<Trans>Which output has the call?</Trans>} size="md">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          <Trans>Pick the one whose bars move when someone's talking.</Trans>
        </Text>
        {targets.length === 0 ? (
          <Text size="sm" c="dimmed">
            <Trans>No audio outputs found.</Trans>
          </Text>
        ) : (
          targets.map((t) => (
            <button
              type="button"
              key={t.id}
              className={classes.row}
              data-selected={t.id === currentId}
              onClick={() => onSelect(t.id)}
            >
              <span className={classes.dot} data-selected={t.id === currentId} />
              <span className={classes.name}>
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" fw={600} lineClamp={1}>
                    {t.name}
                  </Text>
                  {t.is_default && (
                    <Badge size="xs" color="brand" variant="light">
                      <Trans>Recommended</Trans>
                    </Badge>
                  )}
                </Group>
              </span>
              <Meter peak={peakFor(t.id)} />
            </button>
          ))
        )}
      </Stack>
    </Modal>
  )
}
